import { CompBannerCrewWelfareModel } from '@rightship-tech/common-ui/components/comp-banner-crew-welfare/comp-banner-crew-welfare';
import { autoinject, transient, computedFrom, useView, PLATFORM } from "aurelia-framework";
import * as Enumerable from "linq";
import { BaseVetOverview } from "./base-vet-overview";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { DueDiligenceApi } from "../../../../../apis/due-diligence.api";
import { LookupClientService } from "@rightship-tech/common-ui/services/lookups/lookup-client.service";
import { LookupValueQuery } from "@rightship-tech/common-ui/constants/lookup.constants";
import { LookupValue, LookupValueRequest } from "@rightship-tech/common-ui/models/lookup.models";
import { VesselApi } from "../../../../../apis/vessel.api";
import { VetStatusEnum, VetRuleOutcomeStepEnum, VetTaskClientModel, VetRecentActivityClientModel, DueDiligenceClientModel, RatingsSummaryModel, DueDiligenceRiskScoreClientModel, GetCollaboratorsResponse, VetRiskScoreModel } from "../../../../../models/due-diligence.models";
import { UserClaimsService } from "@rightship-tech/common-ui/services/basic/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { DueDiligenceClaims } from "../../../../../constants/claims.constants";
import { DueDiligenceDialogConstants } from "../../../../../constants/dialog.constants";
import { CompanyApi } from "../../../../../apis/company.api";
import { GetCompanyResponseModel } from "../../../../../models/company.models";
import { GetVesselResponse } from "../../../../../models/vessel.models";
import { SafetyRatingApi } from '../../../../../apis/safety-rating.api';
import moment from 'moment';
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { SignalFunctions } from '../../../../../constants/signal.constants';
import { RequestClientService, ResponseType } from '@rightship-tech/common-ui/services/basic/request-client.service';
import { SubtaskSourceEnum } from '@rightship-tech/common-ui/models/basic.models';

@autoinject
@transient()
@useView(PLATFORM.moduleName("@rightship-tech/due-diligence-app/views/pages/due-diligence/tabs/overview/due-diligence-overview.html"))
export class DueDiligenceOverview extends BaseVetOverview {

    private _vetStatuses: LookupValue[];
    public item: DueDiligenceClientModel;
    public vessel: GetVesselResponse;
    public company: GetCompanyResponseModel;
    public dueDiligenceHeaderElem: HTMLElement;
    public isEditingEmail: boolean;
    public originalEmail: string;
    public isShowMoreSectionRelatedActivities: boolean;
    public reassignment: VetStatusEnum = VetStatusEnum.DD_DueDiligenceReassignment;
    public unacceptable: VetStatusEnum = VetStatusEnum.DD_Unacceptable;
    public reloadItemFunc: any;
    public completedActivities: VetRecentActivityClientModel[];
    public pendingActivities: VetRecentActivityClientModel[];
    public riskScoreSeperatorShowing: boolean;
    public canManageCollaborators: boolean = false;
    public selectedOutcomes: number[] = [];
    public crewWelfareData: CompBannerCrewWelfareModel;
    public ratingModel: RatingsSummaryModel;
    public riskScores: DueDiligenceRiskScoreClientModel[];
    public collaborators: GetCollaboratorsResponse;    
    public maxCategoryCount: number;
    public horizontalBarTicks: number;
    public distributionColElements: Element[] = [];
    public aiSummary: string;
    public aiSummaryFull: string;
    public aiSummaryError: boolean;
    public aiSummaryLoading: boolean;
    public aiSummaryIsTooLong: boolean;
    public aiSummaryShowingMore: boolean;
    public showAiSummaryInDueDiligence: boolean;

    public riskScoresLegends: { name: string, class: string }[] = [

        { name: "Passed Rules", class: "passed" },
        { name: "Failed Rules", class: "failed" },
        { name: "Pending", class: "pending" }
    ];

    @computedFrom('item', 'item.vetTasks', 'item.vetTasks.length', 'selectedOutcomes', 'selectedOutcomes.length', 'task.vetSuperVetTaskOutcomeId')
    get vetTasks() {
        const tasks = {};
        if (this.item) {
            const filter = this.selectedOutcomes.length > 0;
            let vetTasks = this.item.vetTasks;
            const outcomeIds = this.selectedOutcomes
            if (filter) {
                const pending = outcomeIds.filter(x => x == -1).length > 0;
                vetTasks = vetTasks.filter(task => (pending && (!task.vetSuperVetTaskOutcomeId || task.vetSuperVetTaskOutcomeId <= 0)) || outcomeIds.indexOf(task.vetSuperVetTaskOutcomeId) > -1)
            }
            const items = Enumerable.from(vetTasks).groupBy(x => x.category ? x.category : x.isManual ? "Manual" : "No Category")
                .toArray();
            for (const item of items) {
                tasks[item.key()] = item.where(x => !x.isVisibleToRsVetSupersOnly && !x.isInfoOnlyRule).toArray();
            }
        }
        return tasks;
    }

    @computedFrom("item", "item.vetTasks", "item.vetTasks.length")
    get passed() {
        if (this.item && this.item.vetTasks) {
            return this.item.vetTasks.filter(x => this.getOutcome(x) == VetRuleOutcomeStepEnum.Pass).length;
        }
        return 0;
    }

    @computedFrom("item", "item.vetTasks", "item.vetTasks.length")
    get failed() {
        if (this.item && this.item.vetTasks) {
            return this.item.vetTasks.filter(x => this.getOutcome(x) == VetRuleOutcomeStepEnum.Fail).length;
        }
        return 0;
    }

    @computedFrom("item", "item.vetTasks", "item.vetTasks.length")
    get na() {
        if (this.item && this.item.vetTasks) {
            return this.item.vetTasks.filter(x => this.getOutcome(x) == VetRuleOutcomeStepEnum.NA).length;
        }
        return 0;
    }

    @computedFrom("company", "company.company", "company.company.docSubscore", "item", "item.docSubscoreAtVetOutcome", "item.vetStatusId")
    get docSubscore() {
        const currnetSubscore = this.company?.company?.docSubscore;
        return this.item.docSubscoreAtVetOutcome && this.completed
                ? this.item.docSubscoreAtVetOutcome : currnetSubscore
    }    

    @computedFrom("item", "item.vetStatusId")
    get completed() {
        return this.item && (this.item.vetStatusId == VetStatusEnum.DD_Acceptable || this.item.vetStatusId == VetStatusEnum.DD_Unacceptable);
    }

    shouldShowSafetyScoreAndGhg(statusId: number) {
        return statusId == VetStatusEnum.DD_Acceptable || statusId == VetStatusEnum.DD_Unacceptable;
    }

    get awaitingResponse() {
        return this.item && this.item.vetStatusId == VetStatusEnum.DD_AwaitingResponse;
    }

    get vetStatus() {
        if (this._vetStatuses && this.item)
            return this._vetStatuses.filter(x => x.id == this.item.vetStatusId)[0];

        return null;
    }

    @computedFrom("item", "item.vetStatusHistory", "item.vetStatusHistory.length", "item.vetStatusId")
    get lastVetStatusHistories() {
        if (this.item.vetStatusHistory) {
            return [this.item.vetStatusHistory[0]];
        }
        return null;
    }

    @computedFrom("lastVetStatusHistories", "lastVetStatusHistories.length")
    get lastVetStatusHistoryUnacceptable() {
        return this.lastVetStatusHistories.filter(x => x.statusId == VetStatusEnum.DD_Unacceptable)[0];
    }

    @computedFrom("item", "item.vetForSTS")
    get vetForSTSValue(): string {
        if (this.item.vetForSTS == undefined || this.item.vetForSTS == null) {
            return '';
        } else if (this.item.vetForSTS) {
            return 'Yes'
        } else {
            return 'No'
        }
    }

    @computedFrom("item", "item.vetStatusId")
    get notProcessing() {
        return this.item && this.item.vetStatusId != VetStatusEnum.Processing;
    }

    @computedFrom('item', 'item.vetTasks')
    get vetTaskKeys() {
        if (this.item)
            return Enumerable.from(this.item.vetTasks).where(x => !x.isVisibleToRsVetSupersOnly && !x.isInfoOnlyRule)
                .orderBy(x => x.categoryIsCustomer ? 1 : 2).select(x => x.category ? x.category : x.isManual ? "Manual" : "No Category").distinct().toArray();
        return [];
    }

    @computedFrom('model.isSanctioned', 'model.isFlagFake', 'model.isInGeoPoliticalZone', 'model.hasGeopoliticalConflict')
    get isSanctionedOrGeopoliticalZoneOrFlagFake() {
        return this.ratingModel && (this.ratingModel.isSanctioned || this.ratingModel.isFlagFake || this.ratingModel.isInGeoPoliticalZone || this.ratingModel.hasGeopoliticalConflict);
    }

    @computedFrom("createdDate")
    public get getCreatedDate() {
        return this.item? moment(this.item.createdDate).format("DD MMM YYYY, HH:mmA"): null; 
    }

    @computedFrom("distributionColElements", "distributionColElements.length", "distributionColElements[0].clientWidth")
    get distributionColElementBarWidth(): number {
        return this.distributionColElements[0] ? this.distributionColElements[0].clientWidth / 5 : 0;
    }

    @computedFrom("ratingModel", "completed", "item", "item.inspectionStateAtVetOutcome", "item.rightShipInspectionExpiryDateAtVetOutcome", "lastVetStatusHistories")
    get inspectionModel() {
        if(this.completed)
        {
            let model = { 
                inspectionState: this.item.inspectionStateAtVetOutcome,
                rightShipInspectionExpiryDate: this.item.rightShipInspectionExpiryDateAtVetOutcome,
                asOf: this.inspectionAsOf()
            };

            return model;
        }

        return this.ratingModel;
    }

    @computedFrom("ratingModel", "completed", "item", "item.ratingAtVetOutcome", "item.ratingPlusAtVetOutcome", "item.isRatingVerifiedAtVetOutcome")
    get ghgModel() {
        if(this.completed)
        {
            let model = { 
                rating: this.item.ratingAtVetOutcome,
                ratingPlus: this.item.ratingPlusAtVetOutcome,
                verified: this.item.isRatingVerifiedAtVetOutcome
            };

            return model;
        }

        return this.ratingModel;
    }

    @computedFrom("ratingModel", "completed", "item", "item.starRatingAtVetOutcome", "item.safetyRatingTypeAtVetOutcome", "item.incidentGracePeriodsAtVetOutcome")
    get safetyScoreModel() {
        if(this.completed)
        {
            let model = { 
                starRating: this.item.starRatingAtVetOutcome,
                safetyRatingIndicative: null,
                safetyRatingType: this.item.safetyRatingTypeAtVetOutcome,
                incidentGracePeriods: this.item.incidentGracePeriodsAtVetOutcome,
            };

            return model;
        }

        return this.ratingModel;
    }

    inspectionAsOf(): Date {
        const asOf = this.lastVetStatusHistories
            .map(x => x.statusId == VetStatusEnum.DD_Acceptable || x.statusId == VetStatusEnum.DD_Unacceptable ?
                    x.createdDate : null
                );
        return asOf? asOf[0]: null;
    }

    constructor(private dueDiligenceApi: DueDiligenceApi, vesselApi: VesselApi, private dialogClientService: DialogClientService,
        private lookupClientService: LookupClientService, configService: ConfigService, userClaimsClientService: UserClaimsService,
        private companyApi: CompanyApi, private safetyRatingApi: SafetyRatingApi, private signalClientService: SignalClientService,
        private requestClientService: RequestClientService) {   
        super(vesselApi, configService);

        this.canManageCollaborators = userClaimsClientService.hasPermission(DueDiligenceClaims.CanManageCollaborators)
        this.showAiSummaryInDueDiligence = userClaimsClientService.hasPermission(DueDiligenceClaims.ShowAiSummaryInDueDiligence)
    }

    activate(model: any) {
        super.activate(model);

        //Get vet statuses
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.VetStatuses;
        request.parameters = "1,3";
        this.lookupClientService.getLookup(request).then(x => {
            this._vetStatuses = x.activeValues;
        });

        this.item = model.item;
        this.dueDiligenceHeaderElem = model.dueDiligenceHeaderElem;
        this.setCategories();
        this.loadRecentActivities();
    }

    async attached() {

        //Get rating model
        this.ratingModel = await this.safetyRatingApi.getRatingsModel(this.item.vesselId);

        //Get risk scores
        await this.getRiskScores();

        //Get collaborators
        this.collaborators = await this.dueDiligenceApi.getCollaborators(this.item.vetId);

        //Subscribe to vet task update
		this.signalClientService.subscribe(SignalFunctions.vetTaskUpdateChanged, this.onVetTaskUpdated);
		this.signalClientService.subscribe(SignalFunctions.aiSummaryChanged, this.onAiSummaryChanged);
        
        //Get AI Summary
        await this.loadAiSummary();
    }

    async loadAiSummary() {
        if (this.item.aiSummaryFile){
            try {
                this.aiSummaryLoading = true;
                var fileName = `${this.item.aiSummaryFile}.html`;
                const path = `rs-vetting-comments/${fileName}`;
                var sasToken = await this.dueDiligenceApi.getSharedAccessSignatureTokenFromPath(path, fileName);
                let url = `${this.configService.config.urls.blobStorageUrl}/${path}`;
                if (sasToken) {
                    url += ((sasToken.startsWith("?") ? "" : "?") + sasToken);
                }
                this.aiSummary = await this.requestClientService.get(url, {}, ResponseType.DOMString);
                
                //Check if summary is too long
                if (this.aiSummary.length > 1000) {
                    this.aiSummaryFull = this.aiSummary;
                    this.aiSummary = this.aiSummaryFull.substring(0, 1000) + "...";
                    this.aiSummaryIsTooLong = true;
                }
            } 
            catch (error) {
                this.aiSummaryError = true;
                console.error(error);
            }
            finally {
                this.aiSummaryLoading = false;
            }
        }
    }

    detached() {
		this.signalClientService.unsubscribe(SignalFunctions.vetTaskUpdateChanged, this.onVetTaskUpdated);
		this.signalClientService.unsubscribe(SignalFunctions.aiSummaryChanged, this.onAiSummaryChanged);
    }

    onAiSummaryChanged = async (params: { vetId: number, aiSummaryFile:string }) => { 
		if (this.item.vetId != params.vetId) return;
        
        this.item.aiSummaryFile = params.aiSummaryFile;
        //Get AI Summary
        await this.loadAiSummary();
    }

    onVetTaskUpdated = async (params: { vetId: number }) => {
		if (this.item.vetId != params.vetId) return;

        //Get risk scores
        await this.getRiskScores();
        await this.loadRecentActivities();
    }

    private async getRiskScores() {
        this.riskScores = (await this.dueDiligenceApi.getRiskScores(this.item.vetId)).riskScores;
        this.riskScores = Enumerable.from(this.riskScores).where(x => x.name != 'All' && x.name != 'Critical' && x.name != 'Soft')
            .orderByDescending(x => x.countHighFail).thenByDescending(x => x.countMediumFail).thenByDescending(x => x.countLowFail)
            .thenByDescending(x => x.countLowPending + x.countMediumPending + x.countHighPending)
            .thenByDescending(x => x.countLowPass + x.countMediumPass + x.countHighPass)
            .toArray();
        this.maxCategoryCount = Enumerable.from(this.riskScores).select(x => x.countHighPass + x.countLowPass + x.countMediumPass +
            x.countHighPending + x.countLowPending + x.countMediumPending +
            x.countHighFail + x.countLowFail + x.countMediumFail).orderByDescending(x => x).firstOrDefault();
        this.horizontalBarTicks = Math.ceil(this.maxCategoryCount / 5.0);
    }

    public async setVessel(vessel: GetVesselResponse, item: DueDiligenceClientModel) {
        this.vessel = vessel;
        
        //Get company
        this.company = await this.companyApi.getCompany(vessel.vessel.documentOfComplianceCompanyId);

        //Set Crew Welfare data
        this.crewWelfareData = {
            crewWelfareStatus: item.crewWelfareStatusAtVetOutcome && this.completed? item.crewWelfareStatusAtVetOutcome: this.company.crewWelfareStatus,
            docCompanyName: this.company.company.fullName,
            docCompanyId: this.company.company.companyId,
            crewWelfareValidUntil: this.item?.crewWelfareValidUntilAtVetOutcome && this.completed ? item.crewWelfareValidUntilAtVetOutcome?.toISOString(): this.company.crewWelfareValidUntil,
            isLegacyCrewWelfare: this.company.isLegacyCrewWelfare,
            crewWelfareGuid: this.company.crewWelfareGuid,
        };
    }

    //Manage collaborators
    public manageCollaborators() {
        this.dialogClientService.show(DueDiligenceDialogConstants.ManageCollaboratorsDialog, 
            { vetId: this.item.vetId, collaborators: this.collaborators, vessel: this.vessel.vessel }, 
            true, false, false, 'right');
    }

    //Load recent activities
    public async loadRecentActivities() {
        if (this.item) {
            const activities = await this.dueDiligenceApi.getVetRecentActivity(this.item.vetId, false, false, false, SubtaskSourceEnum.MessageCentre);
            activities.forEach(x => { x.title = (x.title == "RFI" ? x.title = "Message" : x.title); });
            this.completedActivities = activities.filter(x => !x.isOpen);
            this.pendingActivities = activities.filter(x => x.isOpen);
        }
    }

    isLink(type: string) {
        return this.dueDiligenceApi.isLink(type);
    }

    generateRecentLink(title: string, id: number, type: string) {
        return this.dueDiligenceApi.generateRecentLink(this.item.vetId, title, id, type);
    }

    isCategoryCustomer(cat: string) {
        return Enumerable.from(this.item.vetTasks).where(x => x.category == cat).any(x => x.categoryIsCustomer);
    }

    getTitle(taskGroup) {
        const x = Number(taskGroup) as VetRuleOutcomeStepEnum;
        switch (x) {
            case VetRuleOutcomeStepEnum.Pass:
                return "Passed Rules";
            case VetRuleOutcomeStepEnum.Fail:
                return "Failed Rules";
            case VetRuleOutcomeStepEnum.NA:
                return "Not Applicable Rules";
        }
    }

    filterClick(outcome: { id: number; name: string }) {
        const index = this.selectedOutcomes.indexOf(outcome.id);
        if (index >= 0) {
            this.selectedOutcomes.splice(index, 1);
        }
        else {
            this.selectedOutcomes.push(outcome.id);
        }
    }

    private getOutcome(x: VetTaskClientModel) {
        return x.vetSuperVetTaskOutcomeId ? x.vetSuperVetTaskOutcomeId : x.vetTaskOutcomeId;
    }

    categories: { id: number; name: string }[] = [];

    @computedFrom('item', 'item.vetTasks', 'task.vetSuperVetTaskOutcomeId')
    get outcomes(): { id: number; name: string }[] {
        const list = [];
        if (this.item && this.item.vetTasks) {
            const vetTasks: VetTaskClientModel[] = this.item.vetTasks;
            const pending = vetTasks.filter(task => (!task.vetSuperVetTaskOutcomeId || task.vetSuperVetTaskOutcomeId <= 0) && !task.isInfoOnlyRule).length;
            const passed = vetTasks.filter(task => task.vetSuperVetTaskOutcomeId == VetRuleOutcomeStepEnum.Pass).length;
            const failed = vetTasks.filter(task => task.vetSuperVetTaskOutcomeId == VetRuleOutcomeStepEnum.Fail).length;
            const notApplicable = vetTasks.filter(task => task.vetSuperVetTaskOutcomeId == VetRuleOutcomeStepEnum.NA).length;

            list.push({ id: -1, name: `Pending (${pending})` });
            list.push({ id: VetRuleOutcomeStepEnum.Pass, name: `Passed (${passed})` });
            list.push({ id: VetRuleOutcomeStepEnum.Fail, name: `Failed (${failed})` });
            list.push({ id: VetRuleOutcomeStepEnum.NA, name: `Not Applicable (${notApplicable})` });
        }

        return list;
    }

    setCategories() {
        const vetTasks: VetTaskClientModel[] = Enumerable.from(this.vetTasks).selectMany(x => <VetTaskClientModel[]>x.value).toArray();
        this.categories = Enumerable.from(vetTasks.map(x => {
            return {
                id: x.isManual ? -1 : x.categoryId,
                name: x.isManual ? "Manual" : x.category
            };
        })).groupBy(x => x.id).select(x => x.firstOrDefault()).where(x => x != null).toArray();
    }

    getTasks() {
        return this.item.vetTasks
            .map(x => {
                return {
                    vetTaskId: x.vetTaskId,
                    name: x.title,
                    comment: x.isInfoOnlyRule ? x.infoVettingComment
                        : x.vetSuperVetTaskOutcomeId == VetRuleOutcomeStepEnum.Pass ? x.passedVettingComment
                            : x.vetSuperVetTaskOutcomeId == VetRuleOutcomeStepEnum.Fail ? x.failedVettingComment
                                : x.naVettingComment,
                    passedComment: x.passedVettingComment,
                    failedComment: x.failedVettingComment,
                    forceIncludeComment: x.forceIncludeComment,
                    isInfoOnlyRule: x.isInfoOnlyRule,
                    infoComment: x.infoVettingComment,
                    naComment: x.naVettingComment,
                    outcomeId: x.vetSuperVetTaskOutcomeId
                };
            })
    }

    @computedFrom("lastVetStatusHistory", "lastVetStatusHistory.user")
    get lastVetStatusHistoryUsers() {
        return this.lastVetStatusHistories.map(x => x.user);
    }

    showMoreSectionRelatedActivities() {
        this.isShowMoreSectionRelatedActivities = !this.isShowMoreSectionRelatedActivities;
    }

    //Show seperator the first time its not "Critical" or "Soft" rule within the loop
    showSeperatorForRiskScores(risk: string) {
        if (risk == "Critical") {
            this.riskScoreSeperatorShowing = true;
        }
        else if (risk == "Soft") {
            this.riskScoreSeperatorShowing = true;
        }
        else if (this.riskScoreSeperatorShowing) {
            return true;
        }
    }

    //Get the top position of the sidebar
    @computedFrom('dueDiligenceHeaderElem')
    public get getSidebarTop(): string {
        if (this.dueDiligenceHeaderElem) {

            //Adding minus (-) as we need to move the sidebar up and adding '56' as thats the height of the tabs
            return "-" + this.dueDiligenceHeaderElem.clientHeight + "px";
        }
        
        return "0";
    }

    //Open all rules dialog
    public openAllRulesDialog() {
        this.dialogClientService.show(DueDiligenceDialogConstants.AllRulesDialog, {
            riskCategoryId: null,
            categoryName: null,
            vetId: this.item.vetId,
            vesselName: this.item.vesselName,
            vesselImo: this.item.vesselImo
        }, true, false, false, 'right');
    }

    //Open risk analysis rules dialog
    public openRiskAnalysisRulesDialog(riskScore: VetRiskScoreModel) {
        this.dialogClientService.show(DueDiligenceDialogConstants.AllRulesDialog,
            {
                riskCategoryId: riskScore.riskCategoryId,
                categoryName: riskScore.name,
                vetId: this.item.vetId,
                vesselName: this.item.vesselName,
                vesselImo: this.item.vesselImo
            }, true, false, false, "right");
    }

    //Read more AI Summary
    public readMoreAiSummary() {
        this.aiSummaryShowingMore = !this.aiSummaryShowingMore;
        this.aiSummary = this.aiSummaryShowingMore ? this.aiSummaryFull : this.aiSummaryFull.substring(0, 1000) + "...";
    }
}